// Bug real: cada formatter de fecha/hora del panel usaba `new Intl.DateTimeFormat('es-AR', {...})`
// SIN `timeZone` — el locale 'es-AR' solo define el orden día/mes/año y los separadores, NO fija
// el huso horario; sin `timeZone` explícito, Intl usa el huso del runtime (el navegador/SO de
// quien mira la pantalla). Resultado: dos operadores viendo la misma solicitud en husos distintos
// veían horas distintas. Backend/DB ya son correctos (Instant en UTC) — esto era puramente un bug
// de presentación. Fix: fijar siempre America/Argentina/Buenos_Aires acá, un solo lugar.
export function createArgentinaDateTimeFormat(
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat('es-AR', {
    ...options,
    timeZone: 'America/Argentina/Buenos_Aires',
  });
}

// One display format per granularity (DESIGN.md: es-AR, `dd/mm/aaaa hh:mm`). Built from parts so
// the output never depends on the runtime's ICU punctuation ("01/10/2026, 01:25 p. m.").
const DATE_TIME_PARTS = createArgentinaDateTimeFormat({
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});
const MONTH_SHORT = createArgentinaDateTimeFormat({ month: 'short' });
const MONTHS_SHORT = Array.from({ length: 12 }, (_, i) =>
  // Mid-month noon UTC: the same month in Argentina for every index.
  MONTH_SHORT.format(new Date(Date.UTC(2026, i, 15, 12))).replace('.', ''),
);

function parts(iso: string | Date): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of DATE_TIME_PARTS.formatToParts(new Date(iso))) {
    out[p.type] = p.value;
  }
  // Some ICU builds answer "24" for midnight even with h23.
  if (out['hour'] === '24') {
    out['hour'] = '00';
  }
  return out;
}

/** "01/10/2026 13:25" — Argentina time, 24 h. */
export function formatArDateTime(iso: string | Date): string {
  const p = parts(iso);
  return `${p['day']}/${p['month']}/${p['year']} ${p['hour']}:${p['minute']}`;
}

/**
 * "01/05/1990" — a calendar day. A date-only value ("1990-05-01", a Java LocalDate such as
 * dateOfBirth) has no instant, so its components are used as they come: `new Date("1990-05-01")`
 * is UTC midnight, which Argentina time (UTC-3) would show as the day before. Any other value is
 * an instant and is shown as its day in Argentina.
 */
export function formatArDate(iso: string | Date): string {
  const dateOnly = typeof iso === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso) : null;
  if (dateOnly) {
    return `${dateOnly[3]}/${dateOnly[2]}/${dateOnly[1]}`;
  }
  const p = parts(iso);
  return `${p['day']}/${p['month']}/${p['year']}`;
}

/** "ago 2026" — short month and year of an instant, Argentina time. */
export function formatArMonthYear(iso: string | Date): string {
  const p = parts(iso);
  return `${MONTHS_SHORT[Number(p['month']) - 1]} ${p['year']}`;
}

/** BCRA period "202608" → "ago 2026"; anything else is returned as it came. */
export function formatBcraPeriod(periodo: string): string {
  const match = /^(\d{4})(\d{2})$/.exec(periodo);
  const month = match ? Number(match[2]) : 0;
  return match && month >= 1 && month <= 12 ? `${MONTHS_SHORT[month - 1]} ${match[1]}` : periodo;
}

/** "13:25:07.042" — log timestamps, Argentina time, with milliseconds. */
export function formatArTimeMs(iso: string | Date): string {
  const p = parts(iso);
  const ms = String(new Date(iso).getUTCMilliseconds()).padStart(3, '0');
  return `${p['hour']}:${p['minute']}:${p['second']}.${ms}`;
}
